import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { useSelector } from "react-redux";
import { dispatch, getState } from "../../state/store";
import {
  resetSelectedApp,
  resetSelectedAppMarketsToAdd,
  resetSelectedAppMarketsToDelete,
  setActivePage,
  setAppsError,
  setAppsRefresh,
  setIsGeneralInfoSaved,
  setIsSavingPending,
  setLastActiveIndex,
  setManageType,
  setOriginalAppLink,
  setOriginalAppName,
  setOriginalAvailableDashboards,
  setOriginalLocalizations,
  setOriginalMarketLocalizations,
  setSelectedApp,
  setUsersRefresh
} from "../../state/ducks";

import {
  alignItems,
  appManageTypes,
  appsConfigurationOptions,
  appUserRoles,
  routePaths,
  strCode
} from "../../consts";
import { appsService, appUserService, log, notificationService, t } from "../../services";
import { withSection } from "../../hoc";
import { appsUtil, arrayUtil, objectUtil, responseUtil } from "../../utils";

import {
  AppManagementBreadcrumbs,
  DiscardModal,
  FlexContainers,
  PageContainer,
  SmallButton,
  TabSelector,
  WhiteContainer,
  WrapperContainer
} from "../../components";
import AppsManagementMenu from "./components/AppsManagementMenu";
import { AppManagementContainer, AppNameTitle } from "./styled";
import TabHeader from "./components/TabHeader";
import { selectAppsError } from "../../state/selectors";

const AppsConfigurationPage = () => {
  const [name, setName] = useState("");
  const [storageObject, setStorageObject] = useState({});
  const [disabled, setDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [confirmDeleteModalActive, setConfirmDeleteModalActive] = useState(false);

  const activePage = useSelector((store) => store.apps.activePage);
  const lastActiveIndex = useSelector((store) => store.apps.lastActiveIndex);
  const isGeneralInfoSaved = useSelector((store) => store.apps.isGeneralInfoSaved);
  const selectedApp = useSelector((store) => store.apps.selectedApp);
  const manageType = useSelector((store) => store.apps.manageType);
  const selectedAppMarketsToAdd = useSelector((store) => store.apps.selectedAppMarketsToAdd);
  const selectedAppMarketsToDelete = useSelector((store) => store.apps.selectedAppMarketsToDelete);
  const originalAvailableDashboards = useSelector(
    (store) => store.apps.originalAvailableDashboards
  );
  const originalEmailMessage = useSelector((store) => store.apps.originalEmailMessage);
  const originalAppName = useSelector((store) => store.apps.originalAppName);
  const originalAppLink = useSelector((store) => store.apps.originalAppLink);
  const originalMarketLocalizations = useSelector(
    (store) => store.apps.originalMarketLocalizations
  );
  const originalLocalizations = useSelector((store) => store.apps.originalLocalizations);
  const isOriginalEmailSetted = useSelector((store) => store.apps.isOriginalEmailSetted);
  const isSavingPending = useSelector((store) => store.apps.isSavingPending);

  const availableConfigurationOptions = useMemo(() => {
    if (
      appUserService.hasRole(appUserRoles.Admin) ||
      appUserService.hasRole(appUserRoles.ApplicationManager) ||
      appUserService.hasRole(appUserRoles.ContentManager)
    ) {
      return appsConfigurationOptions;
    }

    return [];
  }, []);

  const activePageTitle = availableConfigurationOptions.find(
    (page) => page.id === activePage
  )?.title;
  const isAppCreated = manageType !== appManageTypes.EDIT;
  const isUpdate = manageType === appManageTypes.EDIT || activePage < lastActiveIndex;
  const isEdit = isUpdate && manageType === appManageTypes.EDIT;

  const pages = availableConfigurationOptions.reduce((acc, item) => {
    acc[item.title] = item.title;
    return acc;
  }, {});

  const isDeleteAvailable = isEdit && appUserService.hasRole(appUserRoles.Admin);

  const history = useHistory();

  const marketLocalizations = appsUtil.filterMarketLocalizations(selectedApp.marketLocalizations);

  const hasChanges =
    !arrayUtil.arrayEquals(
      originalAvailableDashboards.map((d) => `${d.name} - ${d.isSelected}`),
      (selectedApp.availableDashboards || []).map((d) => `${d.name} - ${d.isSelected}`)
    ) ||
    (isOriginalEmailSetted && originalEmailMessage !== selectedApp.emailMessage) ||
    originalAppName !== selectedApp?.name ||
    originalAppLink !== selectedApp?.link ||
    storageObject.isDisabled !== selectedApp?.isDisabled ||
    !objectUtil.objEquals(originalMarketLocalizations, marketLocalizations) ||
    !objectUtil.objEquals(originalLocalizations, selectedApp?.localizations);

  useEffect(() => {
    if (selectedApp.name && isEdit) {
      dispatch(setOriginalAppName(selectedApp?.name));
      dispatch(setOriginalAppLink(selectedApp?.link));
      dispatch(setOriginalMarketLocalizations(selectedApp?.marketLocalizations));
      dispatch(setOriginalLocalizations(selectedApp?.localizations));

      setName(selectedApp.name);
    }
  }, [selectedApp?.name, selectedApp?.link, isEdit, manageType]);

  const getRequestData = (page) => {
    switch (page) {
      case pages.GENERAL_TITLE:
        return {
          appType: selectedApp.appType,
          name: selectedApp?.name,
          link: selectedApp?.link,
          isDisabled: selectedApp?.isDisabled ?? false,
          availableDashboards: [],
          groupConfigs: [],
          defaultSettings: selectedApp?.defaultSettings || [],
          emailMessage: manageType === appManageTypes.FROM_EXISTING ? selectedApp.emailMessage : ""
        };

      case pages.MARKETS_TITLE:
        return { selectedAppMarketsToAdd, selectedAppMarketsToDelete };

      case pages.DASHBOARDS_TITLE:
        return !selectedApp?.availableDashboards?.length
          ? null
          : {
              appType: selectedApp.appType,
              name: selectedApp?.name,
              availableDashboards: selectedApp?.availableDashboards
            };

      case pages.CUSTOMIZE_EMAIL_TITLE:
        return {
          appType: selectedApp.appType,
          name: selectedApp?.name,
          emailMessage: selectedApp?.emailMessage
        };

      default:
        break;
    }
  };

  const handleManageApp = async () => {
    dispatch(setAppsError(null));

    const data = getRequestData(activePageTitle);

    if (activePage === availableConfigurationOptions.length - 1) {
      setLoading(true);
      await saveAndClose();

      setLoading(false);
      return;
    }

    if (
      activePage <= lastActiveIndex &&
      [pages.MARKETS_TITLE, pages.ROLES_TITLE].includes(activePageTitle)
    ) {
      dispatch(
        setActivePage(
          activePage < availableConfigurationOptions.length ? activePage + 1 : activePage
        )
      );

      if (activePage === lastActiveIndex) {
        dispatch(setLastActiveIndex(activePage + 1));
      }

      return;
    }

    if (
      !isGeneralInfoSaved &&
      (manageType === appManageTypes.FROM_EXISTING ||
        manageType === appManageTypes.FROM_SCRATCH ||
        !manageType)
    ) {
      try {
        setLoading(true);
        await appsService.createApp(data);

        setStorageObject({
          name: selectedApp?.name,
          link: selectedApp?.link,
          appType: selectedApp?.appType
        });
        setName(selectedApp?.name);

        if (manageType !== appManageTypes.FROM_EXISTING) {
          dispatch(setSelectedApp(data));
        }

        dispatch(setIsGeneralInfoSaved(true));
        dispatch(setOriginalAppName(selectedApp?.name));
        dispatch(setOriginalAppLink(selectedApp?.link));
      } finally {
        setLoading(false);
      }
    } else {
      if (data) {
        setLoading(true);
        const res = await appsService.updateApp(data);
        setLoading(false);

        if (responseUtil.isResponseSuccessful(res)) {
          notificationService.showSuccess({
            title: t(strCode.APP_UPDATED_SHORT_TITLTE),
            message: t(strCode.APP_UPDATED_LONG_TITLTE, { name: selectedApp.name }),
            immediate: false
          });

          setName(selectedApp.name);
          setStorageObject((prev) => {
            return manageType === appManageTypes.EDIT
              ? { ...selectedApp }
              : { ...selectedApp, appType: prev.appType };
          });
        }
      }
    }

    const error = selectAppsError()(getState());

    if (!error) {
      dispatch(
        setActivePage(
          activePage < availableConfigurationOptions.length ? activePage + 1 : activePage
        )
      );

      if (activePage === lastActiveIndex) {
        dispatch(
          setLastActiveIndex(
            manageType === appManageTypes.FROM_EXISTING
              ? availableConfigurationOptions.length - 1
              : activePage + 1
          )
        );
      }
    }
  };

  const handleMarketsSave = async ({ selectedAppMarketsToAdd, selectedAppMarketsToDelete }) => {
    if (selectedAppMarketsToAdd?.length) {
      setLoading(true);
      try {
        const resArr = await Promise.all(
          selectedAppMarketsToAdd.map((market) => {
            return appsService.createAppTypeMarketConfig(market);
          })
        );

        if (resArr.every((res) => responseUtil.isResponseSuccessful(res))) {
          notificationService.showSuccess({
            title: t(strCode.APP_MARKETS_CREATED_TITLE),
            message: t(strCode.APP_MARKETS_CREATED_DESCRIPTION)
          });

          const selectedAppMarkets = selectedApp?.marketConfigs || [];

          dispatch(
            setSelectedApp({
              ...selectedApp,
              marketConfigs: [...selectedAppMarkets, ...selectedAppMarketsToAdd]
            })
          );
        }
      } catch (err) {
        log.error("AppsConfigurationPage: Failed to create markets", err);

        notificationService.showError({
          title: t(strCode.APP_MARKETS_CREATE_ERROR_TITLE),
          message: err?.response?.data?.message ? err.response.data.message : err?.toString()
        });
      } finally {
        dispatch(resetSelectedAppMarketsToAdd());
        setLoading(false);
      }
    }

    if (selectedAppMarketsToDelete?.length) {
      try {
        setLoading(true);
        const resArr = await Promise.all(
          selectedAppMarketsToDelete.map((market) => {
            return appsService.deleteAppTypeMarketConfig(market);
          })
        );

        if (resArr.every((res) => responseUtil.isResponseSuccessful(res))) {
          notificationService.showSuccess({
            title: t(strCode.APP_MARKETS_DELETED_TITLE),
            message: t(strCode.APP_MARKETS_DELETED_DESCRIPTION)
          });

          const newAppMarkets = selectedApp?.marketConfigs.filter(
            (appMarket) =>
              !selectedAppMarketsToDelete.some((market) => market.market === appMarket.market)
          );

          const newMarketLocalizations = selectedApp?.marketLocalizations?.filter(
            (el) => !selectedAppMarketsToDelete.some((market) => market.market === el.market)
          );

          dispatch(
            setSelectedApp({
              ...selectedApp,
              marketConfigs: newAppMarkets,
              marketLocalizations: newMarketLocalizations
            })
          );
        }
      } catch (err) {
        log.error("AppsConfigurationPage: Failed to delete markets", err);
        notificationService.showError({
          title: t(strCode.APP_MARKETS_DELETE_ERROR_TITLE),
          message: err?.response?.data?.message ? err.response.data.message : err?.toString()
        });
      } finally {
        dispatch(resetSelectedAppMarketsToDelete());
        setLoading(false);
      }
    }
  };

  const saveAndClose = async () => {
    setLoading(true);
    // If creating from scratch, and (app has not saved dashboards or not saved email template)
    if (
      manageType !== appManageTypes.EDIT &&
      ((originalAvailableDashboards.length &&
        !arrayUtil.arrayEquals(
          (selectedApp?.availableDashboards || []).map((d) => d.isSelected),
          originalAvailableDashboards.map((d) => d.isSelected)
        )) ||
        originalEmailMessage !== selectedApp?.emailMessage)
    ) {
      dispatch(setAppsError(null));
      const res = await appsService.updateApp({
        appType: selectedApp.appType,
        name: selectedApp?.name,
        emailMessage: selectedApp?.emailMessage
      });

      if (responseUtil.isResponseSuccessful(res)) {
        notificationService.showSuccess({
          title: t(strCode.APP_UPDATED_SHORT_TITLTE),
          message: t(strCode.APP_UPDATED_LONG_TITLTE, { name: selectedApp.name }),
          immediate: false
        });
      }
    }

    const appsError = selectAppsError()(getState());
    setLoading(false);

    if (!appsError) {
      await handleCancel();
    } else {
      dispatch(setAppsError(null));
    }
  };

  const handleCancel = async () => {
    if (isSavingPending) {
      return;
    }

    setLoading(true);
    
    if ((isGeneralInfoSaved || isUpdate) && hasChanges) {
      dispatch(setIsSavingPending(true));
      dispatch(setAppsError(null));

      const res = await appsService.updateApp({
        appType: selectedApp.appType,
        name: selectedApp.name,
        link: selectedApp.link,
        availableDashboards: selectedApp.availableDashboards,
        emailMessage: selectedApp.emailMessage,
        localizations: selectedApp.localizations,
        defaultSettings: selectedApp.defaultSettings,
        isDisabled: selectedApp.isDisabled,
        marketLocalizations
      });

      if (responseUtil.isResponseSuccessful(res)) {
        notificationService.showSuccess({
          title: t(strCode.APP_UPDATED_SHORT_TITLTE),
          message: t(strCode.APP_UPDATED_LONG_TITLTE, { name: selectedApp?.name }),
          immediate: false
        });
      }

      dispatch(setIsSavingPending(false));
    }

    const appsError = selectAppsError()(getState());
    setLoading(false);

    if (!appsError) {
      dispatch(setOriginalAvailableDashboards(selectedApp?.availableDashboards));

      dispatch(setAppsRefresh(true));
      dispatch(resetSelectedApp());
      dispatch(setManageType(null));
      dispatch(setAppsError(null));
      history.push(routePaths.APPS);
    } else {
      dispatch(setAppsError(null));
    }
  };

  const onDiscard = () => {
    dispatch(setOriginalAvailableDashboards(selectedApp?.availableDashboards));

    dispatch(setAppsRefresh(false));
    dispatch(resetSelectedApp());
    dispatch(setManageType(null));
    dispatch(setAppsError(null));

    history.push(routePaths.APPS);
  };

  const onDelete = async () => {
    setLoading(true);

    const res = await appsService.deleteApp(selectedApp.appType);

    if (responseUtil.isResponseSuccessful(res)) {
      notificationService.showSuccess({
        title: t(strCode.DELETE_APP_SUCCESS_TITLE),
        message: t(strCode.DELETE_APP_SUCCESS_TEXT)
      });

      dispatch(setAppsRefresh(true));
      dispatch(setUsersRefresh(true));

      setConfirmDeleteModalActive(false);
      setLoading(false);

      history.push(routePaths.APPS);
    }
  };

  useEffect(() => {
    setDisabled(!selectedApp?.name && !storageObject?.name);
  }, [storageObject, selectedApp]);

  useEffect(() => {
    if (manageType === appManageTypes.EDIT && Object.keys(storageObject).length === 0) {
      setStorageObject(selectedApp);
    }

    if (manageType === appManageTypes.EDIT && !name) {
      setName(selectedApp.name);
    }
  }, [manageType]);

  useEffect(() => {
    handleMarketsSave(getRequestData(activePageTitle));
  }, [isEdit, selectedAppMarketsToAdd.length, selectedAppMarketsToDelete.length]);

  return (
    <PageContainer alignItems={alignItems.start}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnBetween}
        alignItems={alignItems.start}
        height={"100%"}
        width="100%">
        <WrapperContainer width={"100%"}>
          <AppManagementBreadcrumbs />

          <AppNameTitle>
            {isGeneralInfoSaved || manageType === appManageTypes.EDIT
              ? originalAppName
              : t(strCode.NEW_APP_TITLE)}
          </AppNameTitle>

          <WhiteContainer style={{ height: "min-content", width: "100%" }}>
            <WrapperContainer
              width={"100%"}
              height={"100%"}
              Container={FlexContainers.FlexRowStart}
              alignItems={"flex-start"}
              pv={24}
              pl={24}>
              <TabSelector
                pages={availableConfigurationOptions}
                activePage={activePage}
                setActivePage={(page) => dispatch(setActivePage(page))}
                disabled={manageType === appManageTypes.EDIT ? false : !isGeneralInfoSaved}
                lastActiveIndex={
                  manageType === appManageTypes.EDIT
                    ? availableConfigurationOptions.length - 1
                    : lastActiveIndex
                }
              />

              <AppManagementContainer
                width={"100%"}
                height={"100%"}
                Container={FlexContainers.FlexColumnStart}
                pl={24}
                alignItems={"flex-start"}>
                <TabHeader
                  title={t(activePageTitle)}
                  dashboardsTitle={t(pages.DASHBOARDS_TITLE)}
                  emailTitle={t(pages.CUSTOMIZE_EMAIL_TITLE)}
                />
                <AppsManagementMenu
                  page={activePageTitle}
                  lastActiveIndex={lastActiveIndex}
                  activePage={activePage}
                />
              </AppManagementContainer>
            </WrapperContainer>
          </WhiteContainer>
        </WrapperContainer>

        <WrapperContainer Container={FlexContainers.FlexRowBetween} gap={8} width="100%">
          <WrapperContainer pt={24}>
            {isDeleteAvailable && (
              <SmallButton
                secondary
                isRedText
                onClick={() => setConfirmDeleteModalActive(true)}
                disabled={disabled || loading}>
                {t(strCode.DELETE_BTN)}
              </SmallButton>
            )}
          </WrapperContainer>

          <WrapperContainer
            Container={
              isDeleteAvailable ? FlexContainers.FlexRowEnd : FlexContainers.FlexRowBetween
            }
            gap={8}
            pt={24}
            width="100%">
            <SmallButton secondary onClick={onDiscard} disabled={loading}>
              {t(strCode.DISCARD_BTN)}
            </SmallButton>

            {isEdit ? (
              <SmallButton
                onClick={handleCancel}
                disabled={loading || !hasChanges}
                loading={!confirmDeleteModalActive && loading}>
                {t(strCode.SAVE_AND_CLOSE_BTN)}
              </SmallButton>
            ) : (
              <WrapperContainer Container={FlexContainers.FlexRow} gap={8}>
                <SmallButton
                  secondary={isAppCreated}
                  disabled={disabled || !isGeneralInfoSaved || loading}
                  onClick={saveAndClose}>
                  {t(strCode.FINISH_BTN)}
                </SmallButton>
                {isAppCreated && (
                  <WrapperContainer width={125}>
                    <SmallButton
                      fullWidth
                      disabled={
                        disabled ||
                        activePage === availableConfigurationOptions.length - 1 ||
                        loading
                      }
                      loading={loading}
                      onClick={handleManageApp}>
                      {t(strCode.NEXT_BTN)}
                    </SmallButton>
                  </WrapperContainer>
                )}
              </WrapperContainer>
            )}
          </WrapperContainer>
        </WrapperContainer>
      </WrapperContainer>

      <DiscardModal
        title={t(strCode.DELETE_APP_CONFIRMATION_TITLE)}
        text={t(strCode.DELETE_APP_CONFIRMATION_TEXT, { appType: selectedApp.name })}
        confirmBtnText={strCode.DELETE_BTN}
        active={confirmDeleteModalActive}
        setActive={setConfirmDeleteModalActive}
        onDiscard={onDelete}
        loading={loading}
      />
    </PageContainer>
  );
};

export default withSection(AppsConfigurationPage);
